import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Session restore via httpOnly cookie — only probe if a session was previously established,
    // so first-time visitors don't see a 401 in the console.
    if (!localStorage.getItem("has_session")) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("has_session"))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("has_session", "1");
    if (!data.user?.must_change_password) setUser(data.user);
    return data;
  }, []);

  const completeLogin = useCallback((u) => {
    localStorage.setItem("has_session", "1");
    setUser(u);
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    return data; // account is pending admin approval — no session is created
  }, []);

  const logout = useCallback(() => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("has_session");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, completeLogin }),
    [user, loading, login, register, logout, completeLogin]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};
